/**
 * Width math for the table view — the single source of truth for "how many
 * tables fit side by side", shared by the table-view header (strip vs spread),
 * the multi-table carousel, and the narrow handoff.
 *
 * The experience is WIDTH-driven, not pointer-driven (design call, Jason
 * 2026-08-15): a 500px desktop window and a phone are the same situation — one
 * table in view, the navigation strip on top, swipe/fan to switch. Pointer
 * type (`useSupportsHover`) still decides desktop-only affordances like the
 * All Tables grid entry, but never how many tables the player is looking at.
 */

/** The narrowest a table may render — an iPhone-13 logical width. */
export const TABLE_MIN_WIDTH_PX = 390;

/** Space BETWEEN tables (a property of the row, not of any table). */
export const TABLE_GAP_PX = 16;

/** How many tables fit a given viewport width at the floor width + gaps. */
export const tablesThatFit = (viewportWidthPx: number): number =>
  Math.max(1, Math.floor((viewportWidthPx + TABLE_GAP_PX) / (TABLE_MIN_WIDTH_PX + TABLE_GAP_PX)));

/**
 * Widest viewport that still fits only ONE table (795px with a 390 floor and
 * 16px gap — two tables need 2×390 + 16 = 796). At or below this, the table
 * view is the phone experience regardless of device: one felt, the navigation
 * strip on top, swipe to switch, and no table-count control (there is nothing
 * to choose between).
 */
export const NARROW_MAX_PX = 2 * TABLE_MIN_WIDTH_PX + TABLE_GAP_PX - 1;

/** @deprecated Prefer NARROW_MAX_PX — the old name implied a separate view. */
export const SINGLE_TABLE_VIEW_MAX_PX = NARROW_MAX_PX;

/** Media query the narrow-table-view hook listens to. */
export const NARROW_TABLE_VIEW_MEDIA_QUERY = `(max-width: ${NARROW_MAX_PX}px)`;

/**
 * Hard ceiling on tables in view at once, however wide the display.
 *
 * Not a layout limit — a 3000px monitor fits seven 390px tables — but a human
 * one. Past four felts a player cannot actually read the action on all of them
 * before a timer runs out, and offering a fifth invites a seat they will play
 * badly. MAX_OPEN_TABLES caps how many may be OPEN; this caps how many may be
 * looked at simultaneously.
 */
export const MAX_TABLES_IN_VIEW = 4;

/**
 * Feature flag: render 2-4 tables as tiles INSIDE the single-table carousel.
 *
 * OFF for the 2026-08-17 demo and the Wednesday release. The tiled surface
 * re-creates problems the All Tables grid already solves — per-table modals are
 * contained to their cell there, the control rail has a settled home below the
 * strip band, and each cell forces portrait — and re-solving them under a
 * deadline is exactly how 0.0.205/206 went out wrong twice. While OFF, the
 * carousel always shows ONE felt and the grid page is the multi-table surface;
 * the table-count control routes between them. The tiled code paths stay in
 * place (and tested) so the port can resume deliberately.
 */
export const TILED_TABLE_VIEW_ENABLED = false;

/**
 * The counts the player may choose between right now: 1 up to whichever runs
 * out first — the width, the tables they actually have open, or the ceiling.
 *
 * Offering a count that cannot be honoured is the failure mode this exists to
 * prevent. A menu listing "4 tables" on a 900px window has to either lie or
 * silently correct itself, and both teach the player not to trust the control.
 * Two open tables on a wide display offer [1, 2] — never a "3" that would
 * render an empty column.
 */
export const tableCountOptions = (viewportWidthPx: number, openTables: number): number[] => {
  const ceiling = Math.min(tablesThatFit(viewportWidthPx), openTables, MAX_TABLES_IN_VIEW);
  return Array.from({ length: Math.max(1, ceiling) }, (_, i) => i + 1);
};

/** Conservative row-wide allowance for fractional cell sizing. */
export const CELL_WIDTH_ROUNDING_GUARD_PX = Math.ceil(MAX_TABLES_IN_VIEW / 2);

/**
 * Leaves a little slack when cells fill the row. This does not bound overflow
 * from descendants; the grid reserves scrollbar space separately (EN-10800).
 */
export const cellFlexBasis = (tablesAcross: number): string =>
  `calc((100% - ${
    (tablesAcross - 1) * TABLE_GAP_PX + CELL_WIDTH_ROUNDING_GUARD_PX
  }px) / ${tablesAcross})`;

/**
 * Clamp a remembered preference to what is currently possible.
 *
 * The choice is persisted, so it outlives the window it was made in: pick 4 on
 * a wide monitor, reopen on a laptop, and the preference must quietly become
 * what fits rather than breaking the layout. Widening again restores it,
 * because the CHOICE is what is stored, not the clamped result.
 */
/**
 * Where the window of visible tables should start, given the one the player is
 * focused on.
 *
 * The window MOVES AS LITTLE AS POSSIBLE. If the focused table is already on
 * screen the window does not shift at all — tapping the second of two visible
 * felts must not slide the row underneath the tap. Only when focus lands
 * outside the window does it scroll, and then just far enough to bring that
 * table to the near edge.
 *
 * Re-centring on every focus change would be the obvious alternative and it is
 * wrong: it moves tables the player did not ask to move, and at two-in-view it
 * would shuffle the row every time they clicked between the two felts they were
 * already watching.
 */
export const windowStartFor = (
  currentStart: number,
  focusedIndex: number,
  tablesInView: number,
  totalTables: number
): number => {
  const size = Math.max(1, Math.min(tablesInView, Math.max(1, totalTables)));
  const maxStart = Math.max(0, totalTables - size);
  let start = Math.min(Math.max(0, currentStart), maxStart);
  if (focusedIndex < 0) return start;
  // Scroll only far enough to expose the focused table at whichever edge it
  // fell off.
  if (focusedIndex < start) start = focusedIndex;
  else if (focusedIndex > start + size - 1) start = focusedIndex - size + 1;
  return Math.min(Math.max(0, start), maxStart);
};

export const clampTablesInView = (
  chosen: number | null | undefined,
  viewportWidthPx: number,
  openTables: number
): number => {
  const options = tableCountOptions(viewportWidthPx, openTables);
  const ceiling = options[options.length - 1];
  // Anything that is not a usable number means "no choice recorded" — null,
  // undefined, and the NaN a corrupted localStorage entry parses to. Falling
  // through with one of those would return NaN and lay out nothing at all, so
  // the absent case and the malformed case get the same safe answer.
  if (typeof chosen !== 'number' || !Number.isFinite(chosen)) return ceiling;
  return Math.min(Math.max(1, Math.floor(chosen)), ceiling);
};
